import {
  type ChangeAcknowledgement,
  acknowledgementOf,
  CATEGORIES,
  CUSTOM_STUDY_TYPE_ID,
  type CategoryId,
  type StoredStudyTypeSuggestion,
  type StudyTypeId,
  type StudyTypeSelection,
  categoryById,
  studyTypeById,
  studyTypeName,
  studyTypesInCategory,
} from '@sigora/domain';
import { type ChangeConsequences } from '@sigora/domain';
import { useEffect, useState } from 'react';

import { setStudyType } from '../api/client.ts';
import { ChangeConfirm, unacknowledgedChangeOf } from './ChangeConsequences.tsx';
import { StudyTypeSuggestion } from './StudyTypeSuggestion.tsx';

/** Read out after the name of the area that holds the study's type. */
const STUDY_TYPE_AREA_HOLDS_CHOICE = ', holds your study type';

export interface StudyTypePickerProps {
  studyId: string;
  /** What the study has chosen, or nothing while nobody has chosen. */
  selection: StudyTypeSelection | null;
  /** Run once the study has stored a new type, so the study is read again. */
  onStudyChanged: () => void;
  /** What Sigora made of the last sentence the researcher wrote, or nothing while there is none. */
  suggestion?: StoredStudyTypeSuggestion | null;
}

/** The choice a click stands for, kept so a refused one can be made again unchanged. */
interface Choice {
  studyTypeId: StudyTypeId;
  categoryId?: CategoryId;
}

/** Whether what the study now reports is the choice that was made. */
const isStored = (selection: StudyTypeSelection | null, choice: Choice): boolean =>
  selection !== null &&
  selection.id === choice.studyTypeId &&
  (selection.category ?? undefined) === choice.categoryId;

/** Which Category a stored choice was made inside, or nothing while nothing is stored. */
const categoryOf = (selection: StudyTypeSelection | null): CategoryId | null => {
  if (selection === null) {
    return null;
  }

  // Custom carries the Category it was started from; a named type carries none, because the
  // catalogue is the authority on where it sits.
  return selection.category ?? studyTypeById(selection.id)?.categoryId ?? null;
};

/** Which Category a choice is made inside: the one it names, or else the catalogue's. */
const categoryOfChoice = (choice: Choice): CategoryId | null =>
  choice.categoryId ?? studyTypeById(choice.studyTypeId)?.categoryId ?? null;

/**
 * Choosing what kind of study this is: an area first, then a shape within it.
 *
 * One accordion, because they are one decision: the areas narrow the sixteen down to a readable
 * handful, the open area shows its shapes directly beneath it while the others shrink to their
 * names, and opening another area or closing this one is a button rather than a navigation.
 * Nothing here is a route: the step's URL addresses the step, not a position inside
 * it, so a reader who reloads lands on whatever the study has stored rather than halfway through
 * a choice they did not finish making.
 *
 * Every card is a plain button, so the keyboard needs nothing written here to operate it. An area
 * carries `aria-expanded`, since pressing it opens or closes its shapes, and the area holding the
 * study's type is marked selected; a shape carries `aria-pressed`, since pressing it chooses.
 *
 * A study that can no longer be edited is not pre-empted: the panel above says the study is
 * frozen, and the server refuses the write. Deciding here as well would be a second opinion drawn
 * from a study read that may be a poll behind the one the server holds.
 */
export const StudyTypePicker = ({
  studyId,
  selection,
  onStudyChanged,
  suggestion = null,
}: StudyTypePickerProps) => {
  /**
   * The area that is open.
   *
   * `undefined` follows the study, which is what a first render and a reload want; a Category id
   * is one the reader opened; `null` is every area closed, though the study may still hold a
   * choice made inside one.
   */
  const [browsing, setBrowsing] = useState<CategoryId | null | undefined>(undefined);
  /**
   * The choice being made, from the click until the study reports it.
   *
   * It outlives the write on purpose. What the screen says about the choice — which card is
   * pressed, and the line naming the type — is read off the study, which is re-read after the
   * write rather than during it; ending the saving state at the write would leave a beat showing
   * neither the old answer nor the new one, with every card live to be clicked again.
   */
  const [saving, setSaving] = useState<{ choice: Choice; stored: boolean } | null>(null);
  const [refused, setRefused] = useState<{ choice: Choice; message: string } | null>(null);
  /** A choice the server will make only once the researcher has seen what it does below it. */
  const [asking, setAsking] = useState<{
    choice: Choice;
    consequences: ChangeConsequences;
  } | null>(null);

  const openCategoryId = browsing === undefined ? categoryOf(selection) : browsing;
  const openCategory = openCategoryId === null ? undefined : categoryById(openCategoryId);
  const chosenName = selection === null ? undefined : studyTypeName(selection.id);

  const choose = (choice: Choice, acknowledgedDownstream: ChangeAcknowledgement | null = null) => {
    setSaving({ choice, stored: false });
    setRefused(null);
    setAsking(null);
    void setStudyType(studyId, choice.studyTypeId, choice.categoryId, acknowledgedDownstream).then(
      () => {
        // The open area becomes the one the stored choice is in, wherever it was made: a type
        // chosen from the suggestion while another area is open is shown under its own. A choice
        // refused or put off leaves open whatever the reader had open.
        setBrowsing(categoryOfChoice(choice));
        // Written, and not yet on screen: what shows it is the study, which is read again now.
        setSaving((current) => (current === null ? null : { ...current, stored: true }));
        onStudyChanged();
      },
      (failure: unknown) => {
        setSaving(null);
        const consequences = unacknowledgedChangeOf(failure);
        if (consequences !== null) {
          setAsking({ choice, consequences });
          return;
        }

        setRefused({
          choice,
          message:
            failure instanceof Error ? failure.message : 'That study type could not be saved.',
        });
      },
    );
  };

  /**
   * The choice is over once the study says so, and not before.
   *
   * Both halves are needed. Waiting only on the study would end a re-pick of the type already
   * stored the moment it began, because the study says so already; waiting only on the write would
   * be the beat this exists to close.
   */
  useEffect(() => {
    if (saving !== null && saving.stored && isStored(selection, saving.choice)) {
      setSaving(null);
    }
  }, [saving, selection]);

  const busy = saving !== null;
  /** No other card is offered while a change waits on the researcher's answer. */
  const deciding = busy || asking !== null;

  return (
    <div className="study-type" data-test-id="study-type-picker">
      {chosenName !== undefined && (
        <p className="study-type-chosen" data-test-id="study-type-chosen">
          Your study type: {chosenName}.
        </p>
      )}

      {refused !== null && (
        <p className="chat-error" data-test-id="study-type-error" role="alert">
          {refused.message}{' '}
          <button
            type="button"
            data-test-id="study-type-retry"
            disabled={busy}
            onClick={() => choose(refused.choice)}
          >
            Try again
          </button>
        </p>
      )}

      {asking !== null && (
        <ChangeConfirm
          consequences={asking.consequences}
          busy={busy}
          onConfirm={() => choose(asking.choice, acknowledgementOf(asking.consequences))}
          onCancel={() => setAsking(null)}
        />
      )}

      {busy && (
        <p className="study-type-saving" data-test-id="study-type-saving">
          Saving your choice…
        </p>
      )}

      <section aria-labelledby="study-type-areas-heading" data-test-id="study-type-categories">
        <h2 className="study-type-heading" id="study-type-areas-heading">
          What are you working on?
        </h2>

        {CATEGORIES.map((category) => {
          const open = openCategory?.id === category.id;
          const panelId = `study-type-area-${category.id}`;
          // While one area is open the others are only their names, so its types stay in view.
          const compact = openCategory !== undefined && !open;
          const holdsChoice = categoryOf(selection) === category.id;

          return (
            <div className="study-type-area" key={category.id}>
              <button
                type="button"
                className={[
                  'study-type-card',
                  'choice',
                  holdsChoice ? 'choice-selected' : '',
                  compact ? 'study-type-card-compact' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                data-test-id={`study-type-category-${category.id}`}
                aria-expanded={open}
                aria-controls={open ? panelId : undefined}
                disabled={deciding}
                onClick={() => setBrowsing(open ? null : category.id)}
              >
                <span className="study-type-card-name">{category.name}</span>
                {/* Said in words as well as in colour, since the row is not a pressed button. */}
                {holdsChoice && (
                  <span className="visually-hidden">{STUDY_TYPE_AREA_HOLDS_CHOICE}</span>
                )}
                {!compact && (
                  <span className="study-type-card-detail">{category.businessQuestion}</span>
                )}
              </button>

              {open && (
                <div
                  className="study-type-area-panel"
                  id={panelId}
                  role="region"
                  aria-label={category.name}
                  data-test-id="study-type-types"
                >
                  <p className="study-type-lede">
                    Which of these is closest? It sets what you bring and what you get back.
                  </p>

                  {studyTypesInCategory(category.id).map((studyType) => (
                    <button
                      type="button"
                      className="study-type-card choice"
                      key={studyType.id}
                      data-test-id={`study-type-${studyType.id}`}
                      aria-pressed={selection?.id === studyType.id}
                      disabled={deciding}
                      onClick={() => choose({ studyTypeId: studyType.id })}
                    >
                      <span className="study-type-card-name">{studyType.name}</span>
                      <span className="study-type-card-detail">{studyType.bringGet}</span>
                    </button>
                  ))}

                  {/*
                    The Category is carried into the choice rather than dropped: a study that is
                    none of these is still a study about this area, and that is the only thing
                    anybody knows about it yet.
                  */}
                  <button
                    type="button"
                    className="study-type-card choice"
                    data-test-id="study-type-none-of-these"
                    aria-pressed={
                      selection?.id === CUSTOM_STUDY_TYPE_ID && selection.category === category.id
                    }
                    disabled={deciding}
                    onClick={() =>
                      choose({ studyTypeId: CUSTOM_STUDY_TYPE_ID, categoryId: category.id })
                    }
                  >
                    <span className="study-type-card-name">
                      Something in this area, but none of these
                    </span>
                    <span className="study-type-card-detail">
                      Sigora asks what you have and offers general goal options.
                    </span>
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {/*
          Below the areas, for the reader none of them fits: they describe the study instead, and
          choose from what Sigora makes of it through the same write the cards use.
        */}
        <StudyTypeSuggestion
          studyId={studyId}
          suggestion={suggestion}
          selection={selection}
          busy={busy}
          onChoose={choose}
          onStudyChanged={onStudyChanged}
        />
      </section>
    </div>
  );
};
